import type { KeyboardEvent, MouseEvent } from 'react';
import { formatMp, formatMpDims } from '../../lib/format';
import type { LocalPhoto } from '../../lib/types';

interface PhotoCardProps {
  photo: LocalPhoto;
  /** True when this photo is in the working selection (drives the gold ring). */
  selected: boolean;
  /** Only 'ready' photos can be selected — busy/error cards are inert. */
  selectable: boolean;
  onToggle: (shiftKey: boolean) => void;
  onRemove: () => void;
  onRetry: () => void;
}

/**
 * Desktop upload grid card — small & dense so hundreds of photos stay scannable.
 * The thumbnail is the whole tile (remove ✕, selection ✓ and upload progress
 * overlay it); the only text is a compact MP caption. Quality messaging lives
 * in Step 2 (Print Check) — the upload grid never warns about resolution.
 * The filename and full dimensions live in the tile's title tooltip.
 */
export function PhotoCard({
  photo,
  selected,
  selectable,
  onToggle,
  onRemove,
  onRetry,
}: PhotoCardProps) {
  const busy = photo.status === 'decoding' || photo.status === 'queued';
  const uploading = photo.status === 'uploading' || photo.status === 'hashing';
  const error = photo.status === 'error';

  function toggle(e: MouseEvent | KeyboardEvent) {
    onToggle('shiftKey' in e ? e.shiftKey : false);
  }
  function onKeyDown(e: KeyboardEvent) {
    if (selectable && (e.key === ' ' || e.key === 'Enter')) {
      e.preventDefault();
      toggle(e);
    }
  }
  // A remove/retry click must never toggle selection or start a marquee.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const title =
    photo.width > 0 ? `${photo.filename} · ${formatMpDims(photo.width, photo.height)}` : photo.filename;

  return (
    <div
      className={`pcard${error ? ' pcard--error' : ''}${selected ? ' pcard--selected' : ''}`}
      data-photo-id={selectable ? photo.id : undefined}
      title={title}
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-pressed={selectable ? selected : undefined}
      aria-label={selectable ? `${selected ? 'Deselect' : 'Select'} ${photo.filename}` : undefined}
      onClick={selectable ? toggle : undefined}
      onKeyDown={selectable ? onKeyDown : undefined}
      onMouseDown={
        selectable
          ? (e) => {
              if (e.shiftKey) e.preventDefault(); // no text selection on shift-click
            }
          : undefined
      }
    >
      <div className="pcard__thumb">
        {photo.previewUrl ? (
          <img className="pcard__img" src={photo.previewUrl} alt={photo.filename} loading="lazy" />
        ) : (
          <div className="pcard__thumb--placeholder" />
        )}
        {selectable && (
          <span className="pcard__check" aria-hidden="true">
            ✓
          </span>
        )}
        {photo.status === 'ready' && (
          <span className="pcard__uploaded" role="img" aria-label="Uploaded" title="Uploaded ✓">
            ✓
          </span>
        )}
        <button
          type="button"
          className="pcard__remove"
          onClick={(e) => {
            stop(e);
            onRemove();
          }}
          onMouseDown={stop}
          aria-label={`Remove ${photo.filename}`}
        >
          ✕
        </button>
        {busy && <div className="pcard__spinner">Reading…</div>}
        {uploading && (
          <div className="pcard__progress" aria-hidden="true">
            <div className="pcard__progress-fill" style={{ width: `${photo.progress}%` }} />
          </div>
        )}
      </div>

      {error ? (
        <span className="pcard__caption pcard__caption--error">
          {photo.error ?? 'Something went wrong.'}
          {photo.file && (
            <>
              {' '}
              <button
                type="button"
                className="linkbtn linkbtn--dark"
                onClick={(e) => {
                  stop(e);
                  onRetry();
                }}
                onMouseDown={stop}
              >
                Retry
              </button>
            </>
          )}
        </span>
      ) : photo.width > 0 ? (
        <span className="pcard__caption">{formatMp(photo.width, photo.height)}</span>
      ) : (
        <span className="pcard__caption">Reading…</span>
      )}
    </div>
  );
}
